import { getAdminToken } from "@/lib/adminToken";
import { useState } from "react";
import { motion } from "framer-motion";
import { Shield, Lock, Eye, EyeOff, CheckCircle2, Key, UserCog } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";

export default function AdminSecurity() {
  const { user } = useAdminAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const token = getAdminToken();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSaved(false);

    if (formData.newPassword !== formData.confirmPassword) {
      setError("New passwords do not match");
      return;
    }

    if (formData.newPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setSaving(true);

    try {
      const res = await fetch("/api/admin/password", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setSaved(true);
        setFormData({ currentPassword: "", newPassword: "", confirmPassword: "" });
        setTimeout(() => setSaved(false), 3000);
      } else {
        setError(data.error || "Failed to update password");
      }
    } catch {
      setError("Network error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span className="w-8 h-0.5 bg-accent rounded-full" />
          <span className="text-accent text-xs font-medium uppercase tracking-wide">Settings</span>
        </div>
        <h1 className="text-2xl font-bold text-primary">Security</h1>
        <p className="text-neutral-400 text-sm mt-1">Manage your account security settings</p>
      </div>

      {saved && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-50 border border-green-100 rounded-xl p-4 flex items-center gap-3 mb-6"
        >
          <CheckCircle2 className="w-5 h-5 text-green-500" />
          <span className="text-green-600 text-sm">Password updated successfully!</span>
        </motion.div>
      )}

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-100 rounded-xl p-4 flex items-center gap-3 mb-6"
        >
          <Shield className="w-5 h-5 text-red-500" />
          <span className="text-red-600 text-sm">{error}</span>
        </motion.div>
      )}

      <div className="space-y-6">
        {/* Change Password */}
        <div className="bg-white rounded-xl border border-neutral-100 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
              <Key className="w-5 h-5 text-accent" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-primary">Change Password</h2>
              <p className="text-xs text-neutral-400">Update your admin account password</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1.5 uppercase tracking-wide">
                Current Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.currentPassword}
                  onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 pr-10 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1.5 uppercase tracking-wide">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={formData.newPassword}
                  onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 pr-10 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1.5 uppercase tracking-wide">
                Confirm New Password
              </label>
              <input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                placeholder="Confirm new password"
              />
            </div>

            <div className="flex items-center justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => setFormData({ currentPassword: "", newPassword: "", confirmPassword: "" })}
                className="px-4 py-2.5 rounded-xl border border-neutral-200 text-neutral-600 hover:bg-neutral-50 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2.5 rounded-xl bg-accent hover:bg-accent/90 text-white transition-colors text-sm font-medium disabled:opacity-70"
              >
                {saving ? "Updating..." : "Update Password"}
              </button>
            </div>
          </form>
        </div>

        {/* Security Info */}
        <div className="bg-white rounded-xl border border-neutral-100 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <UserCog className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-primary">Account Security</h2>
              <p className="text-xs text-neutral-400">Security status and information</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-green-500" />
                <span className="text-sm text-neutral-700">Account Status</span>
              </div>
              <span className="text-xs px-2 py-1 bg-green-100 text-green-600 rounded-full">Active</span>
            </div>

            <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-accent" />
                <span className="text-sm text-neutral-700">Login Method</span>
              </div>
              <span className="text-xs text-neutral-400">Email & Password</span>
            </div>

            <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
              <div className="flex items-center gap-3">
                <UserCog className="w-5 h-5 text-primary" />
                <span className="text-sm text-neutral-700">Account Role</span>
              </div>
              <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full capitalize">
                {user?.role?.replace("-", " ") || "Administrator"}
              </span>
            </div>

            <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
              <div className="flex items-center gap-3">
                <UserCog className="w-5 h-5 text-neutral-400" />
                <span className="text-sm text-neutral-700">Email</span>
              </div>
              <span className="text-xs text-neutral-500">{user?.email || "—"}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
